import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE, WS_BASE } from './config';
import AdminUserManagement from './AdminUserManagement';
import { useTheme } from './metasupply/components/ThemeContext';

interface OverviewStats {
  total_queries: number;
  today_queries: number;
  today_cost: number;
  avg_cost:number;
  total_cost: number;
  success_rate: number;
  unique_ips_today: number;
  avg_response_time_ms: number;
  total_tokens: number;
}

interface DailyStats {
  date: string;
  queries: number;
  cost: number;
  tokens: number;
  unique_ips: number;
  avg_response_ms: number;
  success_rate: number;
}

interface IPStats {
  ip_address: string;
  queries: number;
  cost: number;
  tokens: number;
  last_query: string;
}

interface RecentQuery {
  id: number | string;
  question: string;
  sql: string;
  sql_full?: string;  // Full SQL for modal view
  success: boolean;
  error: string | null;
  result_count: number;
  tokens: number;
  cost: number;
  response_ms: number;
  ip_address: string;
  created_at: string;
  explanation: string | null;
  explanation_full?: string;  // Full explanation for modal view
}

// Patterns that indicate an out-of-scope/rejected question
const OUT_OF_SCOPE_PATTERNS = [
  "not related to pharmaceutical",
  "outside the scope",
  "cannot help with",
  "only answer questions about",
  "pharmaceutical database",
  "don't have information about",
  "not within my scope",
  "cannot provide information",
  "I can only assist with",
  "I'm designed to help with",
  "not something I can help with"
];

const isOutOfScope = (query: RecentQuery): boolean => {
  const textToCheck = (query.explanation || '') + (query.error || '');
  return OUT_OF_SCOPE_PATTERNS.some(pattern =>
    textToCheck.toLowerCase().includes(pattern.toLowerCase())
  );
};

const StatCard = ({ title, value, color, accent }: { title: string; value: string | number; color?: string; accent?: string }) => {
  const { T } = useTheme();
  return (
  <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: "18px 20px", display: "flex", flexDirection: "column", gap: 8, boxShadow: T.shadow, borderTop: accent ? `3px solid ${accent}` : "none" }}>
    <span style={{ fontSize: 11, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>{title}</span>
    <span style={{ fontSize: 26, fontWeight: 800, color: color || T.navy, letterSpacing: "-0.02em" }}>{value}</span>
  </div>
  );
};

const IconSpinner = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: "spin 1s linear infinite" }}>
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

const SectionHeader = ({ children, sub }: { children: React.ReactNode; sub?: string }) => {
  const { T } = useTheme();
  return (
  <div style={{ marginBottom: 4 }}>
    <h2 style={{ fontSize: 24, fontWeight: 800, color: T.navy, letterSpacing: "-0.02em", lineHeight: 1.2 }}>{children}</h2>
    {sub && <p style={{ fontSize: 13, color: T.textLight, marginTop: 6, lineHeight: 1.5 }}>{sub}</p>}
  </div>
  );
};

interface AdminProps {
  onUnauthorized?: (message: string) => void;
  theme?: 'dark' | 'light';
  onToggleTheme?: () => void;
  isAdmin?: boolean;
}

// Modal component for viewing full content
interface DetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  content: string;
  type: 'sql' | 'response';
}

function DetailModal({ isOpen, onClose, title, content, type }: DetailModalProps) {
  const { T } = useTheme();
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback
      const textarea = document.createElement('textarea');
      textarea.value = content;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Must be after all hooks
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{type === 'sql' ? '🔍 SQL Query' : '💬 Response'}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className={`modal-code-block ${type}`}>
            {content || 'No content available'}
          </div>
        </div>
        <div className="modal-footer">
          <button className="copy-btn-large" onClick={copyToClipboard}>
            {copied ? '✓ Copied!' : '📋 Copy to Clipboard'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Admin({ onUnauthorized, theme, onToggleTheme, isAdmin = true }: AdminProps) {
  const { T } = useTheme();
  const [overview, setOverview] = useState<OverviewStats | null>(null);
  const [dailyStats, setDailyStats] = useState<DailyStats[]>([]);
  const [ipStats, setIpStats] = useState<IPStats[]>([]);
  const [recentQueries, setRecentQueries] = useState<RecentQuery[]>([]);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'queries' | 'ips' | 'users' | 'subscriptions' | 'usage'>('overview');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [modalData, setModalData] = useState<{ isOpen: boolean; title: string; content: string; type: 'sql' | 'response' }>({
    isOpen: false,
    title: '',
    content: '',
    type: 'response'
  });

  // Pagination state
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalQueries, setTotalQueries] = useState(0);

  // WebSocket state
  const [wsConnected, setWsConnected] = useState(false);
  const [liveMode, setLiveMode] = useState(true);
  const [newQueryIds, setNewQueryIds] = useState<Set<string>>(new Set());
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentPageRef = useRef(currentPage);
  const pageSizeRef = useRef(pageSize);

  // Keep refs in sync
  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  const openModal = (title: string, content: string, type: 'sql' | 'response') => {
    setModalData({ isOpen: true, title, content, type });
  };

  const closeModal = () => {
    setModalData({ ...modalData, isOpen: false });
  };

  const fetchData = useCallback(async () => {
    try {
      const [overviewRes, dailyRes, ipRes, queriesRes] = await Promise.all([
        fetch(`${API_BASE}/admin/stats/overview`, { credentials: 'include' }),
        fetch(`${API_BASE}/admin/stats/daily?days=7`, { credentials: 'include' }),
        fetch(`${API_BASE}/admin/stats/by-ip?limit=20`, { credentials: 'include' }),
        fetch(`${API_BASE}/admin/queries/recent?limit=${pageSize}&offset=${currentPage * pageSize}`, { credentials: 'include' })
      ]);

      const unauthorized = [overviewRes, dailyRes, ipRes, queriesRes].some(
        (res) => res.status === 401 || res.status === 403
      );

      if (unauthorized) {
        const message = 'Not authorized';
        setAuthError(message);
        if (onUnauthorized) onUnauthorized(message);
        return;
      }

      if (overviewRes.ok) {
        setOverview(await overviewRes.json());
      }
      if (dailyRes.ok) {
        const data = await dailyRes.json();
        setDailyStats(data.data);
      }
      if (ipRes.ok) {
        const data = await ipRes.json();
        setIpStats(data.data);
      }
      if (queriesRes.ok) {
        const data = await queriesRes.json();
        // Clear any temporary "live-" queries and replace with real data from server
        setRecentQueries(data.data);
        setTotalQueries(data.total);
        // Clear temporary IDs from animation tracking
        setNewQueryIds(new Set());
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  }, [pageSize, currentPage, onUnauthorized]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    // Don't auto-refresh if live mode is enabled (WebSocket handles updates)
    if (autoRefresh && !liveMode) {
      const interval = setInterval(fetchData, 10000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, liveMode, fetchData]);

  // WebSocket connection - only depends on liveMode to avoid reconnections
  useEffect(() => {
    // Clear any pending reconnect
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (!liveMode) {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
        setWsConnected(false);
      }
      return;
    }

    // If already connected, don't reconnect
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      return;
    }

    let isMounted = true;

    const connectWebSocket = () => {
      // Don't connect if component unmounted or already connecting/connected
      if (!isMounted) return;
      if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
        return;
      }

      const ws = new WebSocket(`${WS_BASE}/ws/admin`);

      ws.onopen = () => {
        if (!isMounted) {
          ws.close();
          return;
        }
        console.log('WebSocket connected');
        setWsConnected(true);
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        if (isMounted) {
          setWsConnected(false);
          wsRef.current = null;
          // Reconnect after 3 seconds if still in live mode and mounted
          reconnectTimeoutRef.current = setTimeout(() => {
            if (isMounted && liveMode) {
              connectWebSocket();
            }
          }, 3000);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      ws.onmessage = (event) => {
        if (!isMounted) return;
        try {
          const message = JSON.parse(event.data);
          if (message.type === 'new_query') {
            const newQuery = message.data;
            // Generate a unique temporary ID for animation
            const tempId = `live-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

            // Add to recent queries at the top (only if on first page)
            // Use refs to get current values without adding dependencies
            if (currentPageRef.current === 0) {
              setRecentQueries(prev => {
                // Check if this query already exists (by question + timestamp to avoid duplicates)
                const exists = prev.some(q =>
                  q.question === newQuery.question &&
                  q.created_at === newQuery.created_at
                );
                if (exists) {
                  return prev;
                }

                const updated = [{
                  id: tempId,
                  question: newQuery.question,
                  sql: newQuery.sql,
                  sql_full: newQuery.sql,
                  success: newQuery.success,
                  error: newQuery.error || null,
                  result_count: newQuery.result_count,
                  tokens: newQuery.tokens,
                  cost: newQuery.cost,
                  response_ms: Math.round(newQuery.response_ms),
                  ip_address: newQuery.ip_address,
                  created_at: newQuery.created_at,
                  explanation: newQuery.explanation,
                  explanation_full: newQuery.explanation
                } as RecentQuery, ...prev.slice(0, pageSizeRef.current - 1)];
                return updated;
              });

              // Mark as new for animation
              setNewQueryIds(prev => new Set(prev).add(tempId));
              setTimeout(() => {
                setNewQueryIds(prev => {
                  const next = new Set(prev);
                  next.delete(tempId);
                  return next;
                });
              }, 3000);

              // Update total count
              setTotalQueries(prev => prev + 1);
            }

            // Update overview stats
            setOverview(prev => prev ? {
              ...prev,
              total_queries: prev.total_queries + 1,
              today_queries: prev.today_queries + 1,
              today_cost: prev.today_cost + newQuery.cost,
              total_cost: prev.total_cost + newQuery.cost,
              total_tokens: prev.total_tokens + newQuery.tokens
            } : prev);
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      wsRef.current = ws;
    };

    connectWebSocket();

    return () => {
      isMounted = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [liveMode]); // Only depend on liveMode - use refs for other values

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 40, color: T.textLight }}>
        Loading dashboard...
      </div>
    );
  }

  if (authError) {
    return (
      <div style={{ padding: 32 }}>
        <div style={{ color: T.textLight, marginBottom: 16 }}>{authError}</div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <h1 style={{ margin: 0, fontSize: 24, color: T.navy }}>Admin Dashboard</h1>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 12, color: T.textMid }}>
            <span>{wsConnected ? "Live" : "Offline"}</span>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input type="checkbox" checked={liveMode} onChange={(e) => setLiveMode(e.target.checked)} />
              Real-time
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
              Auto-refresh
            </label>
            <button onClick={fetchData} style={{ padding: "8px 12px", borderRadius: T.radiusSm, border: `1px solid ${T.border}`, background: T.white, color: T.textMid, cursor: "pointer" }}>Refresh</button>
          </div>
        </div>

        <SectionHeader sub="Usage, costs, and performance over time">Usage & Query Analytics</SectionHeader>

        {overview && (
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${isAdmin ? 4 : 3}, 1fr)`, gap: 16 }}>
            <StatCard title="Total Queries" value={overview.total_queries} color={T.navy} accent={T.navy} />
            <StatCard title="Today's Queries" value={overview.today_queries} color={T.success} accent={T.success} />
            <StatCard title="Today's Cost" value={`$${overview.today_cost.toFixed(4)}`} color={T.warning} accent={T.warning} />
            {isAdmin && <StatCard title="Total Cost" value={`$${overview.total_cost.toFixed(4)}`} color={T.textDark} accent={T.gold} />}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {[
            { id: 'overview', label: 'Daily Stats' },
            { id: 'queries', label: 'Recent Queries' },
            { id: 'ips', label: 'By IP Address' },
            ...(isAdmin ? [
              { id: 'users', label: 'Users' },
              { id: 'subscriptions', label: 'Subscriptions' },
              { id: 'usage', label: 'Token Usage' },
              { id: 'self-attestations', label: 'Self Attestations', route: '/self-attestations' },
              { id: 'data-ingestion', label: 'Data Ingestion', route: '/ingest' },
              { id: 'scraper-history', label: 'Scraper History', route: '/scraper-history' },
              { id: 'agent-activity', label: 'Agent Activity', route: '/agent-activity' },
            ] : []),
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                if ('route' in tab && tab.route) {
                  navigate(tab.route);
                } else {
                  setActiveTab(tab.id as 'overview' | 'queries' | 'ips' | 'users' | 'subscriptions' | 'usage');
                }
              }}
              style={{
                padding: "8px 16px",
                borderRadius: T.radiusSm,
                border: `1px solid ${T.border}`,
                background: activeTab === tab.id ? T.navy : T.white,
                color: activeTab === tab.id ? T.white : T.textMid,
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 16, boxShadow: T.shadow }}>
          {activeTab === 'overview' && (
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: T.navy, marginBottom: 12 }}>Last 7 Days</h3>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: T.offWhite }}>
                      {["Date", "Queries", "Cost", "Tokens", "Unique IPs", "Avg Response", "Success Rate"].map(h => (
                        <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600, color: T.textDark, borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dailyStats.map(day => (
                      <tr key={day.date} style={{ borderBottom: `1px solid ${T.borderLight}` }}>
                        <td style={{ padding: "10px 12px" }}>{day.date}</td>
                        <td style={{ padding: "10px 12px" }}>{day.queries}</td>
                        <td style={{ padding: "10px 12px" }}>${day.cost.toFixed(4)}</td>
                        <td style={{ padding: "10px 12px" }}>{day.tokens.toLocaleString()}</td>
                        <td style={{ padding: "10px 12px" }}>{day.unique_ips}</td>
                        <td style={{ padding: "10px 12px" }}>{day.avg_response_ms}ms</td>
                        <td style={{ padding: "10px 12px" }}>{day.success_rate}%</td>
                      </tr>
                    ))}
                    {dailyStats.length === 0 && (
                      <tr>
                        <td colSpan={7} style={{ padding: 16, textAlign: "center", color: T.textLight }}>
                          No daily stats yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'queries' && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 10 }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: T.navy }}>Recent Queries</h3>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 12, color: T.textMid }}>
                  <span>Showing {currentPage * pageSize + 1}-{Math.min((currentPage + 1) * pageSize, totalQueries)} of {totalQueries}</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(0);
                    }}
                    style={{ padding: "6px 10px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.white, color: T.textMid, fontSize: 12 }}
                  >
                    {[10, 20, 30, 50].map(size => <option key={size} value={size}>{size}</option>)}
                  </select>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button disabled={currentPage === 0} onClick={() => setCurrentPage(0)} style={{ padding: "4px 8px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.offWhite, cursor: "pointer" }}>«</button>
                    <button disabled={currentPage === 0} onClick={() => setCurrentPage(p => Math.max(0, p - 1))} style={{ padding: "4px 8px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.offWhite, cursor: "pointer" }}>‹</button>
                    <button disabled={(currentPage + 1) * pageSize >= totalQueries} onClick={() => setCurrentPage(p => p + 1)} style={{ padding: "4px 8px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.offWhite, cursor: "pointer" }}>›</button>
                    <button disabled={(currentPage + 1) * pageSize >= totalQueries} onClick={() => setCurrentPage(Math.ceil(totalQueries / pageSize) - 1)} style={{ padding: "4px 8px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.offWhite, cursor: "pointer" }}>»</button>
                  </div>
                </div>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: T.offWhite }}>
                      {["Time", "Question", "Status", "Results", "Tokens", "Cost", "Response", "IP"].map(h => (
                        <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600, color: T.textDark, borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {recentQueries.map(query => (
                      <tr key={query.id} style={{ borderBottom: `1px solid ${T.borderLight}` }}>
                        <td style={{ padding: "10px 12px", color: T.textLight, whiteSpace: "nowrap" }}>{new Date(query.created_at).toLocaleString()}</td>
                        <td style={{ padding: "10px 12px", color: T.textMid, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{query.question}</td>
                        <td style={{ padding: "10px 12px", color: query.success ? T.success : T.danger, fontWeight: 600 }}>{query.success ? "OK" : "ERR"}</td>
                        <td style={{ padding: "10px 12px" }}>{query.result_count ?? '-'}</td>
                        <td style={{ padding: "10px 12px" }}>{query.tokens}</td>
                        <td style={{ padding: "10px 12px" }}>${query.cost.toFixed(4)}</td>
                        <td style={{ padding: "10px 12px" }}>{query.response_ms}ms</td>
                        <td style={{ padding: "10px 12px", color: T.textLight, whiteSpace: "nowrap" }}>{query.ip_address || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {(activeTab === 'users' || activeTab === 'subscriptions' || activeTab === 'usage') && (
            <AdminUserManagement activeTab={activeTab} />
          )}

          {activeTab === 'ips' && (
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: T.navy, marginBottom: 12 }}>Usage by IP Address</h3>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: T.offWhite }}>
                      {["IP Address", "Queries", "Cost", "Tokens", "Last Query"].map(h => (
                        <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600, color: T.textDark, borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ipStats.map(ip => (
                      <tr key={ip.ip_address} style={{ borderBottom: `1px solid ${T.borderLight}` }}>
                        <td style={{ padding: "10px 12px", color: T.textMid }}>{ip.ip_address}</td>
                        <td style={{ padding: "10px 12px" }}>{ip.queries}</td>
                        <td style={{ padding: "10px 12px" }}>${ip.cost.toFixed(4)}</td>
                        <td style={{ padding: "10px 12px" }}>{ip.tokens.toLocaleString()}</td>
                        <td style={{ padding: "10px 12px", color: T.textLight }}>{ip.last_query ? new Date(ip.last_query).toLocaleString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <DetailModal
          isOpen={modalData.isOpen}
          onClose={closeModal}
          title={modalData.title}
          content={modalData.content}
          type={modalData.type}
        />
      </div>
  );
}

export default Admin;
